
import {
  signInWithPopup,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  onIdTokenChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updateProfile,
  sendPasswordResetEmail,
  getAdditionalUserInfo,
  type User
} from "firebase/auth";
import { auth, googleProvider } from "./firebase";
import { User as AppUser } from "../types";

// Map Firebase User to our App User type
// Fix: Use the correctly imported User type from firebase/auth modular SDK
const mapUser = (fbUser: User): AppUser => ({
  id: fbUser.uid,
  name: fbUser.displayName || fbUser.email?.split('@')[0] || "User",
  email: fbUser.email || "",
  photoURL: fbUser.photoURL || undefined,
  isPro: false // Default to free, real logic would check DB claims
});

// Fix: Use modular signInWithPopup
export const signInWithGoogle = async (): Promise<AppUser & { isNewUser?: boolean }> => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    const isNewUser = getAdditionalUserInfo(result)?.isNewUser ?? false;
    return { ...mapUser(result.user), isNewUser };
  } catch (error) {
    console.error("Google Sign In Error:", error);
    throw error;
  }
};

// Fix: Use modular createUserWithEmailAndPassword
export const registerWithEmail = async (name: string, email: string, pass: string): Promise<AppUser & { isNewUser?: boolean }> => {
  try {
    const result = await createUserWithEmailAndPassword(auth, email, pass);
    await updateProfile(result.user, { displayName: name });
    return { ...mapUser(result.user), isNewUser: true };
  } catch (error) {
    console.error("Registration Error:", error);
    throw error;
  }
};

// Fix: Use modular signInWithEmailAndPassword
export const loginWithEmail = async (email: string, pass: string): Promise<AppUser> => {
  try {
    const result = await signInWithEmailAndPassword(auth, email, pass);
    return mapUser(result.user);
  } catch (error) {
    console.error("Login Error:", error);
    throw error;
  }
};

// Fix: Use modular sendPasswordResetEmail
export const resetPassword = async (email: string): Promise<void> => {
  try {
    // Removed actionCodeSettings as it causes "auth/unauthorized-continue-uri" 
    // if the current domain is not whitelisted in Firebase Console.
    await sendPasswordResetEmail(auth, email);
  } catch (error) {
    console.error("Reset Password Error:", error);
    throw error;
  }
};

// Fix: Use modular signOut
export const logout = async () => {
  try {
    await firebaseSignOut(auth);
  } catch (error) {
    console.error("Logout Error:", error);
  }
};

// Use onIdTokenChanged instead of onAuthStateChanged so we detect token
// refresh failures promptly (fires when the ID token changes or expires).
export const subscribeToAuth = (callback: (user: AppUser | null) => void) => {
  return onIdTokenChanged(auth, (fbUser) => {
    if (fbUser) {
      callback(mapUser(fbUser));
    } else {
      callback(null);
    }
  });
};
